import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

/**
 * Localisation contract: sure-api uses Gedmo Translatable on
 * `DistributionReference.name`. The axios client sends `Accept-Language`
 * from `i18next.language`, so the API already returns `name` localised.
 */
const DistributionReferenceApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
});

type DistributionReferenceApi = z.infer<typeof DistributionReferenceApiSchema>;

export type DistributionReference = {
    id: number;
    code: string;
    label: string;
};

export const useDistributionReference = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

    const query = useQuery({
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["allDistributionReferences", lang],
        queryFn: async () => {
            return await sureApiCall.get("/api/distribution_references", {
                params: { itemsPerPage: 100 },
            });
        },
        select: (request) => {
            const distributions = request.data["hydra:member"];
            const data = zodParseWithLog(
                distributions,
                DistributionReferenceApiSchema.array(),
                "allDistributionReferences",
            );

            return data.map(
                (item: DistributionReferenceApi): DistributionReference => ({
                    id: item.id,
                    code: item.code,
                    label: item.name.trim() !== "" ? item.name : item.code,
                }),
            );
        },
    });

    return {
        distributionReference: query.data,
        ...query,
    };
};
